import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CourseHero from './components/CourseHero';
import CourseSyllabus from './components/CourseSyllabus';
import CourseInstructor from './components/CourseInstructor';
import CourseReviews from './components/CourseReviews';
import CourseSidebar from './components/CourseSidebar';
import CourseSectionNav from './components/CourseSectionNav';
import ReadingProgressBar from '../blog/components/ReadingProgressBar';
import BackToTop from '../blog/components/BackToTop';
import type { Course } from '../../mocks/courses';
import { api } from '@/lib/api';
import { adaptCourseComments, adaptCourseDetail, adaptCourseListItem, createCategoryMetaResolver, createCategoryNameResolver } from '@/lib/adapters';
import { getToken } from '@/lib/storage';

const CourseDetailPage = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [relatedCourses, setRelatedCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!courseId || !/^\d+$/.test(courseId)) {
      setLoading(false);
      return;
    }
    void (async () => {
      setLoading(true);
      try {
        const categories = await api.getCategories();
        const categoryNameOf = createCategoryNameResolver(categories);
        const categoryMetaOf = createCategoryMetaResolver(categories);
        const detail = await api.getCourseDetail(courseId, Boolean(getToken()));
        const adaptedDetail = adaptCourseDetail(detail, categoryNameOf(detail.categoryId), categoryMetaOf(detail.categoryId));
        const comments = await api.getCourseComments(courseId);
        adaptedDetail.courseReviews = adaptCourseComments(comments.list);
        setCourse(adaptedDetail);

        const relatedPage = await api.searchCourses({
          pageCurrent: 1,
          pageSize: 3,
          categoryId: detail.categoryId
        });
        setRelatedCourses(
          relatedPage.list
            .filter((item) => String(item.id) !== courseId)
            .map((item) => adaptCourseListItem(item, categoryNameOf(item.categoryId), categoryMetaOf(item.categoryId)))
        );
      } catch {
        setCourse(null);
        setRelatedCourses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId]);

  const sections = useMemo(() => {
    if (!course) {
      return [];
    }
    return [
      ...(course.learningObjectives?.length ? [{ id: 'section-objectives', label: '你將學到什麼', icon: 'ri-checkbox-circle-line' }] : []),
      ...(course.requirements?.length ? [{ id: 'section-requirements', label: '學習前提', icon: 'ri-list-check-2' }] : []),
      ...(course.courseIncludes?.length ? [{ id: 'section-includes', label: '課程包含', icon: 'ri-stack-line' }] : []),
      ...(course.courseInfo?.length ? [{ id: 'section-info', label: '課程資訊', icon: 'ri-information-line' }] : []),
      ...(course.chapters?.length ? [{ id: 'section-syllabus', label: '課程大綱', icon: 'ri-book-open-line' }] : []),
      ...(course.instructorDetail ? [{ id: 'section-instructor', label: '講師介紹', icon: 'ri-user-star-line' }] : []),
      ...(course.courseReviews?.length ? [{ id: 'section-reviews', label: '學員評價', icon: 'ri-star-line' }] : []),
      { id: 'section-related', label: '同類推薦', icon: 'ri-apps-line' }
    ];
  }, [course]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
        <div className="text-sm text-slate-400">課程資料載入中...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
            <i className="ri-book-open-line text-2xl text-slate-400" />
          </div>
          <p className="text-base font-semibold text-slate-700">找不到課程資訊</p>
          <button
            onClick={() => navigate('/courses')}
            className="mt-4 rounded-xl bg-[#0A1628] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#12233f]"
          >
            返回課程中心
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FC] font-sans">
      <ReadingProgressBar />
      <Navbar />

      <div style={{ paddingTop: '64px' }}>
        <CourseHero course={course} />

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex gap-8 items-start">
            <div className="flex-1 min-w-0 space-y-10">
              {course.learningObjectives?.length ? (
                <div id="section-objectives" className="bg-white border border-slate-200 rounded-2xl p-6 scroll-mt-24">
                  <h2 className="text-xl font-bold text-[#0A1628] mb-4">你將學到什麼</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {course.learningObjectives.map((item) => (
                      <div key={item} className="flex items-start gap-2.5">
                        <i className="ri-checkbox-circle-fill text-emerald-500 text-base mt-0.5" />
                        <span className="text-slate-600 text-sm leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {course.requirements?.length ? (
                <div id="section-requirements" className="scroll-mt-24">
                  <h2 className="text-xl font-bold text-[#0A1628] mb-4">學習前提</h2>
                  <ul className="space-y-2">
                    {course.requirements.map((requirement) => (
                      <li key={requirement} className="flex items-start gap-2.5 text-slate-500 text-sm">
                        <i className="ri-arrow-right-s-line text-slate-400 mt-0.5" />
                        {requirement}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {course.courseIncludes?.length ? (
                <div id="section-includes" className="scroll-mt-24">
                  <h2 className="text-xl font-bold text-[#0A1628] mb-4">課程包含</h2>
                  <ul className="space-y-2">
                    {course.courseIncludes.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-slate-600 text-sm">
                        <i className="ri-checkbox-circle-line text-violet-500 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {course.courseInfo?.length ? (
                <div id="section-info" className="bg-white border border-slate-200 rounded-2xl p-6 scroll-mt-24">
                  <h2 className="text-xl font-bold text-[#0A1628] mb-4">課程資訊</h2>
                  <div className="space-y-3">
                    {course.courseInfo.map((item) => (
                      <div key={`${item.label}-${item.value}`} className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">{item.label}</span>
                        <span className="text-[#0A1628] font-semibold">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {course.chapters?.length ? (
                <div id="section-syllabus" className="scroll-mt-24">
                  <CourseSyllabus chapters={course.chapters} totalDuration={course.duration} />
                </div>
              ) : null}

              {course.instructorDetail ? (
                <div id="section-instructor" className="scroll-mt-24">
                  <CourseInstructor instructor={course.instructorDetail} />
                </div>
              ) : null}

              {course.courseReviews?.length ? (
                <div id="section-reviews" className="scroll-mt-24">
                  <CourseReviews reviews={course.courseReviews} rating={course.rating} totalReviews={course.reviews} />
                </div>
              ) : null}

              <div id="section-related" className="scroll-mt-24">
                <h2 className="text-xl font-bold text-[#0A1628] mb-5">同類推薦</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedCourses.map((related) => (
                    <button
                      key={related.id}
                      onClick={() => navigate(`/course/${related.id}`)}
                      className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-violet-200 transition-all"
                    >
                      <div className={`bg-gradient-to-br ${related.gradient} rounded-xl px-4 py-4 mb-3`}>
                        <h4 className="text-white font-semibold text-sm">{related.title}</h4>
                      </div>
                      <p className="text-xs text-slate-400">{related.instructor}</p>
                      <p className="text-sm font-bold text-[#0A1628] mt-2">${related.price}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-80 flex-shrink-0">
              <div className="sticky top-24">
                <CourseSectionNav sections={sections} />
                <CourseSidebar course={course} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default CourseDetailPage;
